import { z } from 'zod';

// ── Embedded entities ──────────────────────────────────
const SessionPersonApiSchema = z
  .object({
    id_user: z.string(),
    full_name: z.string(),
    email: z.string(),
  })
  .transform(raw => ({
    idUser: raw.id_user,
    fullName: raw.full_name,
    email: raw.email,
  }));

export type SessionPersonDTO = z.infer<typeof SessionPersonApiSchema>;

const SessionTopicTitleApiSchema = z
  .object({
    id_topic_title: z.number(),
    title: z.string(),
  })
  .transform(raw => ({
    idTopicTitle: raw.id_topic_title,
    title: raw.title,
  }));

export type SessionTopicTitleDTO = z.infer<typeof SessionTopicTitleApiSchema>;

const SessionScheduleApiSchema = z
  .object({
    id_schedule: z.number(),
    schedule_day: z.string(),
    schedule_time: z.string(),
    schedule_end: z.string().nullable().optional(),
  })
  .transform(raw => ({
    idSchedule: raw.id_schedule,
    scheduleDay: raw.schedule_day,
    scheduleTime: raw.schedule_time,
    scheduleEnd: raw.schedule_end ?? null,
  }));

export type SessionScheduleDTO = z.infer<typeof SessionScheduleApiSchema>;

// ── Session API shape → transform to DTO ──────────────
const SessionApiSchema = z
  .object({
    id_session: z.string(),
    user: SessionPersonApiSchema.nullable().optional(),
    mentor: SessionPersonApiSchema.nullable().optional(),
    topic_title: SessionTopicTitleApiSchema.nullable().optional(),
    schedule: SessionScheduleApiSchema.nullable().optional(),
    session_start_date: z.string().nullable().optional(),
    session_end_date: z.string().nullable().optional(),
    type: z.string(),
    link_meet: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    session_note: z.string().nullable().optional(),
    session_documentation: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .transform(raw => ({
    idSession: raw.id_session,
    user: raw.user ?? null,
    mentor: raw.mentor ?? null,
    topicTitle: raw.topic_title ?? null,
    schedule: raw.schedule ?? null,
    sessionStartDate: raw.session_start_date ?? null,
    sessionEndDate: raw.session_end_date ?? null,
    type: raw.type,
    linkMeet: raw.link_meet ?? null,
    note: raw.note ?? null,
    sessionNote: raw.session_note ?? null,
    sessionDocumentation: raw.session_documentation ?? null,
    status: raw.status ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }));

export type SessionDTO = z.infer<typeof SessionApiSchema>;

export function toSessionDTO(raw: unknown): SessionDTO {
  return SessionApiSchema.parse(raw);
}

// ── Paginated response ─────────────────────────────────
const GetSessionsApiSchema = z
  .object({
    result: z
      .object({
        data: z.array(SessionApiSchema),
        total_data: z.number(),
        per_page: z.number(),
        current_page: z.number(),
        last_page: z.number(),
        first_page: z.number(),
      })
      .transform(raw => ({
        data: raw.data,
        totalData: raw.total_data,
        perPage: raw.per_page,
        currentPage: raw.current_page,
        lastPage: raw.last_page,
        firstPage: raw.first_page,
      })),
  })
  .transform(raw => raw.result);

export type GetSessionsDTO = z.infer<typeof GetSessionsApiSchema>;

export function toGetSessionsDTO(raw: unknown): GetSessionsDTO {
  return GetSessionsApiSchema.parse(raw);
}

// ── Query params (plain TS type, no Zod) ───────────────
export type SessionStatus =
  | 'incoming'
  | 'no_show_pending'
  | 'awaiting_user_confirmation'
  | 'completed'
  | 'canceled'
  | 'mentor_no_show';

export type GetSessionsParams = {
  page?: number;
  limit?: number;
  search?: string;
  user_id?: string;
  mentor_id?: string;
  status?: SessionStatus;
  start_date?: string;
  end_date?: string;
};
